import { useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";

const Select2Custom = ({
  options = [],
  value = undefined,
  isMulti = false,
  placeholder = "Select...",
  onChange,
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  className = "",
  menuPlacement = "bottom",
  closeMenuOnSelect = true,
  size = "",
  isValid = null,
}) => {
  const [internalValue, setInternalValue] = useState(null);

  // Nếu không có value từ props, dùng internal state
  const actualValue = value !== undefined ? value : internalValue;

  const handleChange = (selectedOption) => {
    if (value === undefined) {
      setInternalValue(selectedOption);
    }

    if (onChange) {
      onChange(selectedOption);
    }
  };

  const getClassNames = () => {
    let classes = `basic-select ${className}`;
    if (size) classes += ` form-control-${size}`;
    if (isValid === true) classes += " is-valid";
    else if (isValid === false) classes += " is-invalid";
    return classes;
  };

  return (
    <Select
      className={getClassNames()}
      classNamePrefix="select"
      value={actualValue}
      onChange={handleChange}
      options={options}
      getOptionValue={(opt) => String(opt?.value)}
      getOptionLabel={(opt) => opt?.label ?? ''}
      isMulti={isMulti}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isSearchable={isSearchable}
      menuPlacement={menuPlacement}
      closeMenuOnSelect={!isMulti || closeMenuOnSelect}
      components={{
        ...(isMulti ? {} : { ClearIndicator: () => null }), // ❌ Ẩn nút "X" nếu không phải multi
      }}
      styles={{
        control: (base, state) => ({
          ...base,
          borderColor: state.isFocused
            ? "#80bdff"
            : isValid === false
            ? "#dc3545"
            : isValid === true
            ? "#28a745"
            : "#ced4da",
          boxShadow: state.isFocused
            ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
            : "none",
          "&:hover": {
            borderColor: state.isFocused
              ? "#80bdff"
              : isValid === false
              ? "#dc3545"
              : isValid === true
              ? "#28a745"
              : "#adb5bd",
          },
          minHeight:
            size === "sm"
              ? "calc(1.5em + 0.5rem + 1px)"
              : size === "lg"
              ? "calc(1.5em + 1rem + 1px)"
              : "calc(1.5em + 0.75rem + 1px)",
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: '#e6f4ff',
          borderRadius: 4,
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: '#000',
          display: 'inline',
          fontSize: '13px',
          padding: '2px 6px'
        }),
        multiValueRemove: (base) => ({
          ...base,
          color: '#333',
          ':hover': { backgroundColor: '#eee', color: '#000' },
        }),
        dropdownIndicator: (base) => ({
          ...base,
          padding: size === "sm" ? "4px" : size === "lg" ? "8px" : "6px",
        }),
        clearIndicator: (base) => ({
          ...base,
          padding: size === "sm" ? "4px" : size === "lg" ? "8px" : "6px",
        }),
        valueContainer: (base) => ({
          ...base,
          padding:
            size === "sm" ? "0 8px" : size === "lg" ? "0 12px" : "0 10px",
        }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
        }),
      }}
      formatOptionLabel={(option, { context }) => {
        const label = option?.label ?? '';
        const thumb = option?.thumb;
        if (!thumb) return label;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: 30, height: 28, marginRight: 8 }}>
            <img
              src={option.thumb}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 4,
              }}
            />
              {option.os && (
                <img
                  src={`/bg-${option.os}.png`}
                  alt=""
                  className="app-logo"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
            <span>{option.label}</span>
          </div>
        );
      }}
    />
  );
};

Select2Custom.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.any, // ✅ optional: nếu không truyền thì dùng nội bộ
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  className: PropTypes.string,
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]),
  closeMenuOnSelect: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "lg", ""]),
  isValid: PropTypes.bool,
};

export default Select2Custom;
